import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { APPLICANT_GROUP_FIELD, SYNTHETIC_UID_FIELD_KEY, SYNTHETIC_UID_OPERATORS, normalizeLegacyOperator } from "@aidkitorg/types/lib/admin-panel/filters";
import React, { useEffect } from "react";
import { Icon } from "../Icon";
import { useLocalizedStrings } from "../LocalizationProvider";
import { Select } from "../Select";
import { useFilterOperators } from "./common";
import { FilterValueInput, isValidDate, isValidNumber } from "./FilterValueInput";
export const FilterCondition = ({ condition, availableFields, onUpdate, onMajorUpdate, onMinorUpdate, onDelete, fetchFieldValues, operatorsByField, }) => {
    if (!availableFields)
        return null;
    const L = useLocalizedStrings();
    const { OPERATORS_BY_TYPE } = useFilterOperators();
    const [localCondition, setLocalCondition] = React.useState(null);
    const getFieldOrApplicantGroup = (fieldKey) => {
        return fieldKey === 'applicant_group'
            ? APPLICANT_GROUP_FIELD
            : availableFields.find(f => f.key === fieldKey);
    };
    const isDateConditionComplete = (field, operator, value) => {
        if (operator === 'last_modified_before' || operator === 'last_modified_after') {
            return value !== undefined && value !== '' && isValidDate(value);
        }
        if (!field || field.type !== 'date')
            return true;
        const operatorConfig = OPERATORS_BY_TYPE[field.type]?.find(op => op.value === operator);
        if (!operatorConfig?.requiresValue)
            return true;
        return value !== undefined && value !== '' && isValidDate(value);
    };
    const isNumberConditionComplete = (field, operator, value) => {
        if (!field || field.type !== 'number')
            return true;
        const operatorConfig = OPERATORS_BY_TYPE[field.type]?.find(op => op.value === operator);
        if (!operatorConfig?.requiresValue)
            return true;
        return value !== undefined && value !== '' && isValidNumber(value);
    };
    const isConditionComplete = (field, operator, value) => {
        return isDateConditionComplete(field, operator, value) && isNumberConditionComplete(field, operator, value);
    };
    const displayCondition = localCondition || condition;
    const selectedField = getFieldOrApplicantGroup(displayCondition.field);
    const allowedOperators = selectedField
        ? (operatorsByField?.[selectedField.key]
            ?? (selectedField.key === SYNTHETIC_UID_FIELD_KEY ? SYNTHETIC_UID_OPERATORS : undefined))
        : undefined;
    const availableOperators = (selectedField ? OPERATORS_BY_TYPE[selectedField.type] : [])
        .filter(op => !allowedOperators || allowedOperators.includes(op.value));
    const availableFieldOptions = availableFields
        .map((f) => ({
        value: f.key,
        label: f.label
    }))
        .sort((a, b) => a.label.localeCompare(b.label));
    const selectedFieldOption = availableFieldOptions.find(f => f.value === selectedField?.key);
    const selectedOperator = availableOperators.find(op => op.value === normalizeLegacyOperator(displayCondition.operator));
    const requiresValue = selectedOperator?.requiresValue ?? true;
    const operatorOptions = availableOperators.map(op => ({
        value: op.value,
        label: op.label
    }));
    const handleUpdate = (updates) => {
        const updatedCondition = {
            ...displayCondition,
            ...updates,
        };
        const fieldForCheck = getFieldOrApplicantGroup(updatedCondition.field);
        const isComplete = isConditionComplete(fieldForCheck, updatedCondition.operator, updatedCondition.value);
        if (!isComplete) {
            setLocalCondition(updatedCondition);
        }
        else {
            setLocalCondition(null);
            onUpdate(updatedCondition);
        }
    };
    const handleMajorUpdate = (updates) => {
        const updatedCondition = {
            ...displayCondition,
            ...updates,
        };
        const fieldForCheck = getFieldOrApplicantGroup(updatedCondition.field);
        const isComplete = isConditionComplete(fieldForCheck, updatedCondition.operator, updatedCondition.value);
        if (!isComplete) {
            setLocalCondition(updatedCondition);
        }
        else {
            setLocalCondition(null);
            onMajorUpdate?.(updatedCondition) || onUpdate(updatedCondition);
        }
    };
    const handleMinorUpdate = (updates) => {
        const updatedCondition = {
            ...displayCondition,
            ...updates,
        };
        const fieldForCheck = getFieldOrApplicantGroup(updatedCondition.field);
        const isComplete = isConditionComplete(fieldForCheck, updatedCondition.operator, updatedCondition.value);
        if (!isComplete) {
            setLocalCondition(updatedCondition);
        }
        else {
            setLocalCondition(null);
            onMinorUpdate?.(updatedCondition) || onUpdate(updatedCondition);
        }
    };
    const handleValidDateUpdate = (updates) => {
        const updatedCondition = {
            ...displayCondition,
            ...updates,
        };
        setLocalCondition(null);
        onMajorUpdate?.(updatedCondition) || onUpdate(updatedCondition);
    };
    const handleValidNumberUpdate = (updates) => {
        const updatedCondition = {
            ...displayCondition,
            ...updates,
        };
        setLocalCondition(null);
        onMajorUpdate?.(updatedCondition) || onUpdate(updatedCondition);
    };
    useEffect(() => {
        if (condition.readOnly)
            return;
        if (!displayCondition.field && availableFields.length > 0) {
            const defaultField = availableFields[0];
            const defaultOperators = OPERATORS_BY_TYPE[defaultField.type] || [];
            const defaultOperator = defaultOperators[0]?.value || 'equals';
            const defaultValue = defaultOperators[0]?.requiresValue ? displayCondition.value : undefined;
            const updates = {
                field: defaultField.key,
                operator: defaultOperator,
                value: defaultValue
            };
            const isComplete = isConditionComplete(defaultField, defaultOperator, defaultValue);
            if (!isComplete) {
                setLocalCondition({
                    ...displayCondition,
                    ...updates
                });
            }
            else {
                onUpdate(updates);
            }
        }
    }, [displayCondition.field, availableFields, onUpdate]);
    if (condition.readOnly) {
        return (_jsx("div", { className: "flex-1 min-w-0", "data-condition-id": condition.id, children: _jsxs("div", { className: "flex items-center gap-2 rounded-md bg-gray-100 text-gray-800 p-2 text-sm border border-gray-400", role: "alert", children: [_jsx(Icon, { role: "decorative", name: "LockClosedIcon", type: "solid", className: "h-4 w-4 shrink-0 text-gray-400" }), _jsx("span", { className: "font-mono", children: condition.description || 'Unsupported filter expression' })] }) }));
    }
    if (displayCondition.field === 'applicant_group') {
        return (_jsx("div", { className: "flex flex-1 min-w-0", "data-condition-id": condition.id, children: _jsxs("div", { className: "flex flex-row gap-2 flex-1 min-w-0 items-start", children: [_jsx("span", { className: "text-sm text-gray-800 whitespace-nowrap shrink-0 pt-1.5", children: L.component_library.filter_panel.is_in_group }), _jsx("div", { className: "w-full sm:w-72 min-w-0", children: _jsx(FilterValueInput, { fieldType: "text", value: displayCondition.value || '', onChange: (value) => handleUpdate({ value }), onDebouncedChange: (value) => handleMinorUpdate({ value }), placeholder: L.component_library.filter_panel.enter_a_value, fieldKey: "applicant_group", onFetchSuggestions: fetchFieldValues, allowFreeText: false }, condition.id) }), onDelete && (_jsx("button", { type: "button", "aria-label": L.component_library.filter_panel.delete, onClick: onDelete, "data-delete-action": true, className: "h-7 w-7 shrink-0 flex items-center justify-center self-center text-gray-400 hover:text-gray-600 transition-colors duration-150", children: _jsx(Icon, { name: "XMarkIcon", type: "solid", size: "md" }) }))] }) }));
    }
    return (_jsx("div", { className: "flex flex-col sm:flex-row gap-2 flex-1 min-w-0", "data-condition-id": condition.id, children: _jsxs("div", { className: "flex flex-col sm:flex-row gap-2 flex-1 min-w-0 items-start", children: [_jsx("div", { className: "w-full sm:flex-[2] min-w-0", children: _jsx(Select, { anchor: "bottom start", virtualized: true, label: L.component_library.filter_panel.select_a_field, description: L.component_library.filter_panel.select_a_field, options: availableFieldOptions, value: selectedFieldOption || undefined, onChange: (field) => {
                            if (!field)
                                return;
                            const newField = getFieldOrApplicantGroup(field.value);
                            const newOperators = newField ? OPERATORS_BY_TYPE[newField.type] : [];
                            const defaultOperator = field.value === 'applicant_group' ? 'equals' : (newOperators[0]?.value || 'equals');
                            let newValue;
                            if (!newOperators[0]?.requiresValue) {
                                newValue = undefined;
                            }
                            else if (newField?.type === 'date') {
                                newValue = "";
                            }
                            else if (newField?.type === 'number') {
                                newValue = "";
                            }
                            else if (field.value === 'applicant_group') {
                                newValue = "";
                            }
                            else {
                                newValue = condition.value ?? "";
                            }
                            const updates = {
                                field: field.value,
                                operator: defaultOperator,
                                value: newValue
                            };
                            handleMajorUpdate(updates);
                        }, hideLabel: true, portal: true, searchable: true, filterFunction: (option, query) => {
                            const queryLower = query.trim().toLowerCase();
                            const labelMatch = String(option.label).trim().toLowerCase().includes(queryLower);
                            const keyMatch = option.value.toString().toLowerCase().includes(queryLower);
                            return labelMatch || keyMatch;
                        } }) }), _jsx("div", { className: "w-full sm:flex-[1] min-w-0", children: selectedField?.key === 'applicant_group' ? (_jsx("span", { className: "text-sm text-gray-800 whitespace-nowrap mt-2 px-2", children: "is" })) : (_jsx(Select, { searchable: true, anchor: "bottom start", options: operatorOptions, value: selectedOperator, onChange: (operator) => {
                            if (!operator)
                                return;
                            const newOperator = availableOperators.find(op => op.value === operator.value);
                            let newValue;
                            if (!newOperator?.requiresValue) {
                                newValue = undefined;
                            }
                            else if (operator.value === 'last_modified_before' || operator.value === 'last_modified_after') {
                                newValue = "";
                            }
                            else if (selectedField?.type === 'number') {
                                newValue = (condition.value && isValidNumber(condition.value)) ? condition.value : "";
                            }
                            else {
                                newValue = condition.value ?? "";
                            }
                            handleMajorUpdate({
                                operator: operator.value,
                                value: newValue
                            });
                        }, description: '', label: L.component_library.filter_panel.filter_condition_operator, labelLocation: 'above', hideLabel: true, portal: true })) }), _jsx("div", { className: "w-full sm:flex-[2] min-w-0", children: requiresValue && (_jsx(FilterValueInput, { fieldType: (displayCondition.operator === 'last_modified_before' || displayCondition.operator === 'last_modified_after') ? 'date' : (selectedField?.type || 'text'), value: displayCondition.value || '', onChange: (value) => handleUpdate({ value }), onDebouncedChange: (value) => handleMinorUpdate({ value }), onValidChange: displayCondition.operator === 'last_modified_before' || displayCondition.operator === 'last_modified_after' || selectedField?.type === 'date' ?
                            (value) => handleValidDateUpdate({ value }) :
                            selectedField?.type === 'number' ?
                                (value) => handleValidNumberUpdate({ value }) : undefined, placeholder: L.component_library.filter_panel.enter_a_value, fieldKey: selectedField?.key, onFetchSuggestions: (displayCondition.operator === 'last_modified_before' || displayCondition.operator === 'last_modified_after') ? undefined :
                            selectedField?.type === 'text' || selectedField?.type === 'user' ? fetchFieldValues : undefined, allowFreeText: selectedField?.key !== 'applicant_group' && selectedField?.type !== 'user' }, condition.id)) }), onDelete && (_jsx("button", { type: "button", "aria-label": L.component_library.filter_panel.delete, onClick: onDelete, "data-delete-action": true, className: "h-7 w-7 shrink-0 flex items-center justify-center self-center text-gray-400 hover:text-gray-600 transition-colors duration-150", children: _jsx(Icon, { name: "XMarkIcon", type: "solid", size: "md" }) }))] }) }));
};
